import { ResourceManager, ResourceStock } from './ResourceManager';
import { BuildingId, BUILDING_DEFINITIONS, BUILDING_ORDER } from '../data/buildings';

// === GESTIONNAIRE D'UPGRADES DE BÂTIMENTS ===
// Gère les niveaux de bâtiments, la file de construction et la persistence.
// Chaque upgrade coûte des ressources + 1 run de temps (termine au retour).

const STORAGE_KEY = 'deadtide_upgrades';

/** Construction en cours */
export interface Construction {
  buildingId: BuildingId;
  targetLevel: number;
}

/** Données sérialisées des upgrades */
interface UpgradeData {
  levels: Record<BuildingId, number>;
  /** Construction en cours (null = rien en construction) */
  construction: Construction | null;
}

export class UpgradeManager {
  private data: UpgradeData;

  constructor() {
    this.data = this.load();
  }

  // === NIVEAUX ===

  /** Niveau actuel d'un bâtiment */
  getBuildingLevel(id: BuildingId): number {
    return this.data.levels[id];
  }

  /** Tous les niveaux */
  getAllLevels(): Record<BuildingId, number> {
    return { ...this.data.levels };
  }

  /** Vérifie si un bâtiment peut être amélioré (niveau max = 3) */
  canUpgrade(id: BuildingId): boolean {
    const level = this.data.levels[id];
    const def = BUILDING_DEFINITIONS[id];
    return level < def.levels.length - 1;
  }

  /** Niveau suivant d'un bâtiment (null si déjà max) */
  getNextLevel(id: BuildingId): number | null {
    if (!this.canUpgrade(id)) return null;
    return this.data.levels[id] + 1;
  }

  /** Coût de l'upgrade suivant (null si pas d'upgrade possible) */
  getUpgradeCost(id: BuildingId): Partial<ResourceStock> | null {
    const next = this.getNextLevel(id);
    if (next === null) return null;
    return BUILDING_DEFINITIONS[id].levels[next].cost ?? null;
  }

  // === CONSTRUCTION ===

  /** Construction en cours */
  getConstruction(): Construction | null {
    return this.data.construction;
  }

  /** Vérifie si on peut lancer une construction */
  isBuilding(): boolean {
    return this.data.construction !== null;
  }

  /**
   * Lance une construction (dépense les ressources).
   * Retourne true si la construction a été lancée.
   */
  startConstruction(id: BuildingId, resources: ResourceManager): boolean {
    // Déjà en construction
    if (this.data.construction) return false;

    // Vérifier qu'on peut upgrade
    const next = this.getNextLevel(id);
    if (next === null) return false;

    // Vérifier et dépenser les ressources
    const cost = BUILDING_DEFINITIONS[id].levels[next].cost;
    if (!cost) return false;
    if (!resources.spend(cost)) return false;

    this.data.construction = { buildingId: id, targetLevel: next };
    this.save();
    return true;
  }

  /**
   * Termine la construction en cours (appelé au retour de run).
   * Retourne le bâtiment amélioré ou null si rien en cours.
   */
  completeConstruction(): Construction | null {
    const c = this.data.construction;
    if (!c) return null;

    this.data.levels[c.buildingId] = c.targetLevel;
    this.data.construction = null;
    this.save();
    return c;
  }

  /** Annule la construction en cours (pas de remboursement) */
  cancelConstruction(): void {
    this.data.construction = null;
    this.save();
  }

  // === PERSISTANCE ===

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // localStorage plein ou indisponible
    }
  }

  private load(): UpgradeData {
    const defaultLevels = this.getDefaultLevels();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        // Fusionner avec les valeurs par défaut (pour les nouveaux bâtiments)
        const levels = { ...defaultLevels } as Record<BuildingId, number>;
        if (data.levels) {
          for (const id of BUILDING_ORDER) {
            if (typeof data.levels[id] === 'number') {
              levels[id] = data.levels[id];
            }
          }
        }
        return {
          levels,
          construction: data.construction ?? null,
        };
      }
    } catch {
      // données corrompues
    }
    return { levels: defaultLevels, construction: null };
  }

  private getDefaultLevels(): Record<BuildingId, number> {
    const levels = {} as Record<BuildingId, number>;
    for (const id of BUILDING_ORDER) {
      levels[id] = BUILDING_DEFINITIONS[id].minLevel;
    }
    return levels;
  }

  /** Reset complet (debug) */
  resetAll(): void {
    this.data = { levels: this.getDefaultLevels(), construction: null };
    this.save();
  }
}
